import { c as _c } from "react/compiler-runtime";
import { match } from 'ts-pattern';
import { memo, useMemo } from 'react';
import { isNotNil } from 'ramda-adjunct';
import { ROW_BORDER_BOTTOM } from '../../../../constants/borderStyles';
import { selectHorizontalSeparatorsVisible, selectVerticalSeparatorsVisible } from '../../../../store/createEagGridStore';
import { useActionsDefinition } from '../../../../store/selectors/useActionsDefinition';
import { useGridSettingsSafe } from '../../../../store/selectors/useGridSettingsSafe';
import { useEagGridSelector } from '../../../../store/useEagGridSelector';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnState } from '../../../../types/EagColumnState';
import type { RowData } from '../../../../types/RowData';
import { getPinnedEdgeShadowClass } from '../../../../utils/getPinnedEdgeShadowClass';
import { LineHeightToPx } from '../../../../utils/lineHeightToValue';
import { GridCell } from './components/GridCell/GridCell';
import { RowActions } from './components/RowActions/RowActions';
import { RowSelectCheckbox } from './components/RowSelectCheckbox/RowSelectCheckbox';

// GridRow reads grid settings and separator visibility from the store once,
// then passes verticalSeparators and valueWrapMode to GridCell as props.
// This avoids per-cell store subscriptions for rarely-changing settings —
// 1 subscription per row instead of 1 per cell.

export type GridRowProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  row: RowData;
  isSelected: boolean;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  isStriped?: boolean;
  rowIndex: number;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  // Selection props — controls whether the row renders a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string) => void;
};

// memo: rendered N times in list; parent GridBody re-renders on loading/selection
// changes that don't affect most rows.
export const GridRow = memo(props => {
  const $ = _c(73);
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);
  const verticalSeparators = useEagGridSelector(selectVerticalSeparatorsVisible);
  const gridSettings = useGridSettingsSafe();
  const heightPx = LineHeightToPx[gridSettings.rowHeight];
  const actionsDefinition = useActionsDefinition();
  const valueWrapMode = gridSettings.valueWrapMode;
  let t0;
  if ($[0] !== props.columnStates) {
    t0 = new Map(props.columnStates.map(_temp));
    $[0] = props.columnStates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const columnStateMap = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== actionsDefinition || $[3] !== columnStateMap || $[4] !== gridSettings.rowHeight || $[5] !== heightPx || $[6] !== horizontalSeparators || $[7] !== props || $[8] !== valueWrapMode || $[9] !== verticalSeparators) {
    let t9;
    if ($[18] !== columnStateMap) {
      t9 = col => {
        const cs_0 = columnStateMap.get(col.key);
        return cs_0?.pinned !== "RIGHT";
      };
      $[18] = columnStateMap;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const nonRightPinnedColumns = props.columns.filter(t9);
    let t10;
    if ($[20] !== columnStateMap) {
      t10 = col_0 => {
        const cs_1 = columnStateMap.get(col_0.key);
        return cs_1?.pinned === "RIGHT";
      };
      $[20] = columnStateMap;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const rightPinnedColumns = props.columns.filter(t10);
    const globalIndexMap = new Map(props.columns.map(_temp2));
    let t11;
    if ($[22] !== props) {
      t11 = event => {
        if (props.onRowClick) {
          props.onRowClick(props.row, event);
        }
      };
      $[22] = props;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    const handleClick = t11;
    let t12;
    if ($[24] !== props.isSelected || $[25] !== props.isStriped) {
      t12 = match({
        isSelected: props.isSelected,
        isStriped: props.isStriped
      }).with({
        isSelected: true
      }, _temp3).with({
        isStriped: true
      }, _temp4).otherwise(_temp5);
      $[24] = props.isSelected;
      $[25] = props.isStriped;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    const t13 = props.onRowClick ? "cursor-pointer" : "";
    let t14;
    if ($[27] !== t12 || $[28] !== t13) {
      t14 = ["group/row", "hover:bg-slate-50", t12, t13].filter(Boolean);
      $[27] = t12;
      $[28] = t13;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    const rowClassName = t14.join(" ");
    let t15;
    if ($[30] !== actionsDefinition || $[31] !== columnStateMap || $[32] !== gridSettings.rowHeight || $[33] !== props.columns.length || $[34] !== props.firstPinnedRightKey || $[35] !== props.lastPinnedLeftKey || $[36] !== props.pinnedLeftOffsets || $[37] !== props.pinnedRightOffsets || $[38] !== props.row || $[39] !== valueWrapMode || $[40] !== verticalSeparators) {
      t15 = function renderCell(column, globalIndex) {
        const columnState = columnStateMap.get(column.key);
        if (!columnState) {
          return null;
        }
        const isLastPinnedLeft = props.lastPinnedLeftKey === column.key;
        const isFirstPinnedRight = props.firstPinnedRightKey === column.key;
        const pinnedEdgeShadowClass = getPinnedEdgeShadowClass(columnState.pinned, isLastPinnedLeft || isFirstPinnedRight);
        const isLastColumn = globalIndex === props.columns.length - 1;
        const isLastBeforeActions = match({
          isLastColumn,
          hasActions: isNotNil(actionsDefinition)
        }).with({
          isLastColumn: true,
          hasActions: true
        }, _temp6).with({
          isLastColumn: true,
          hasActions: false
        }, _temp7).otherwise(_temp8);
        return <GridCell key={column.key} column={column} columnState={columnState} row={props.row} pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)} pinnedRightOffset={props.pinnedRightOffsets.get(column.key)} verticalSeparators={verticalSeparators} valueWrapMode={valueWrapMode} rowHeight={gridSettings.rowHeight} pinnedEdgeShadowClass={pinnedEdgeShadowClass} isLastBeforeActions={isLastBeforeActions} />;
      };
      $[30] = actionsDefinition;
      $[31] = columnStateMap;
      $[32] = gridSettings.rowHeight;
      $[33] = props.columns.length;
      $[34] = props.firstPinnedRightKey;
      $[35] = props.lastPinnedLeftKey;
      $[36] = props.pinnedLeftOffsets;
      $[37] = props.pinnedRightOffsets;
      $[38] = props.row;
      $[39] = valueWrapMode;
      $[40] = verticalSeparators;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    const renderCell = t15;
    t1 = rowClassName;
    const t16 = horizontalSeparators ? ROW_BORDER_BOTTOM : undefined;
    if ($[42] !== heightPx || $[43] !== t16) {
      t2 = {
        height: heightPx,
        ...t16
      };
      $[42] = heightPx;
      $[43] = t16;
      $[44] = t2;
    } else {
      t2 = $[44];
    }
    t3 = handleClick;
    t4 = props.rowIndex;
    if ($[45] !== props.isSelected || $[46] !== props.onToggleSelection || $[47] !== props.row || $[48] !== props.rowIndex || $[49] !== props.rowSelectMode) {
      t5 = props.rowSelectMode !== "NONE" && props.onToggleSelection && <RowSelectCheckbox isSelected={props.isSelected} rowId={props.row.id} onToggleSelection={props.onToggleSelection} rowIndex={props.rowIndex} />;
      $[45] = props.isSelected;
      $[46] = props.onToggleSelection;
      $[47] = props.row;
      $[48] = props.rowIndex;
      $[49] = props.rowSelectMode;
      $[50] = t5;
    } else {
      t5 = $[50];
    }
    t6 = nonRightPinnedColumns.map(column_0 => renderCell(column_0, globalIndexMap.get(column_0.key) ?? 0));
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <td />;
      $[51] = t7;
    } else {
      t7 = $[51];
    }
    t8 = rightPinnedColumns.map(column_1 => renderCell(column_1, globalIndexMap.get(column_1.key) ?? 0));
    $[2] = actionsDefinition;
    $[3] = columnStateMap;
    $[4] = gridSettings.rowHeight;
    $[5] = heightPx;
    $[6] = horizontalSeparators;
    $[7] = props;
    $[8] = valueWrapMode;
    $[9] = verticalSeparators;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
  }
  let t9;
  if ($[52] !== actionsDefinition || $[53] !== gridSettings.rowHeight || $[54] !== heightPx || $[55] !== props.actionColumnWidth || $[56] !== props.disableAllActions || $[57] !== props.isMobile || $[58] !== props.isSelected || $[59] !== props.isStriped || $[60] !== props.onActionClick || $[61] !== props.row) {
    t9 = actionsDefinition && props.onActionClick && props.actionColumnWidth ? <RowActions row={props.row} actionsDefinition={actionsDefinition} onActionClick={props.onActionClick} isMobile={props.isMobile ?? false} rowHeight={gridSettings.rowHeight} actionColumnWidth={props.actionColumnWidth} heightPx={heightPx} isSelected={props.isSelected} isStriped={props.isStriped} isDisabled={props.disableAllActions} /> : null;
    $[52] = actionsDefinition;
    $[53] = gridSettings.rowHeight;
    $[54] = heightPx;
    $[55] = props.actionColumnWidth;
    $[56] = props.disableAllActions;
    $[57] = props.isMobile;
    $[58] = props.isSelected;
    $[59] = props.isStriped;
    $[60] = props.onActionClick;
    $[61] = props.row;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] !== t1 || $[64] !== t2 || $[65] !== t3 || $[66] !== t4 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t10 = <tr className={t1} style={t2} onClick={t3} aria-rowindex={t4}>{t5}{t6}{t7}{t8}{t9}</tr>;
    $[63] = t1;
    $[64] = t2;
    $[65] = t3;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t10;
  } else {
    t10 = $[72];
  }
  return t10;
});
function _temp(cs) {
  return [cs.key, cs];
}
function _temp2(col_1, i) {
  return [col_1.key, i];
}
function _temp3() {
  return "bg-blue-50 hover:bg-blue-100";
}
function _temp4() {
  return "bg-gray-50";
}
function _temp5() {
  return "";
}
function _temp6() {
  return true;
}
function _temp7() {
  return true;
}
function _temp8() {
  return false;
}